import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, CheckCircle, AlertTriangle, XCircle, Server, Database, Zap, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaaSHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHealth(); }, []);

  const fetchHealth = async () => {
    try {
      const res = await axios.get(`${API}/saas/health`);
      setHealth(res.data);
    } catch { toast.error('Failed to load health data'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="saas-loading">Loading system health...</div>;

  const getStatusIcon = (status) => {
    if (status === 'healthy') return <CheckCircle size={20} className="text-green" />;
    if (status === 'degraded') return <AlertTriangle size={20} className="text-yellow" />;
    return <XCircle size={20} className="text-red" />;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#38A169';
    if (score >= 70) return '#D69E2E';
    return '#E53E3E';
  };

  return (
    <div className="saas-page" data-testid="saas-health">
      <div className="saas-page-header">
        <div>
          <h2>System Health</h2>
          <p>Last checked: {health?.last_checked ? new Date(health.last_checked).toLocaleString() : 'N/A'}</p>
        </div>
        <button className="btn-primary" onClick={fetchHealth}>Refresh</button>
      </div>

      <div className="health-grid">
        <div className="health-card">
          <div className="health-card-header">
            <Server size={24} />
            <h3>API Health</h3>
          </div>
          <div className="health-status">
            {getStatusIcon(health?.api_status)}
            <span className={`health-label ${health?.api_status}`}>{health?.api_status || 'unknown'}</span>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-header">
            <Database size={24} />
            <h3>Database Health</h3>
          </div>
          <div className="health-status">
            {getStatusIcon(health?.database_status)}
            <span className={`health-label ${health?.database_status}`}>{health?.database_status || 'unknown'}</span>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-header">
            <Activity size={24} />
            <h3>Provisioning Success</h3>
          </div>
          <div className="health-metric">
            <span className="metric-value" style={{ color: getScoreColor(health?.provisioning_success_rate || 0) }}>
              {health?.provisioning_success_rate || 0}%
            </span>
            <span className="metric-label">Last 7 days</span>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-header">
            <AlertTriangle size={24} />
            <h3>Errors Today</h3>
          </div>
          <div className="health-metric">
            <span className="metric-value" style={{ color: (health?.errors_today || 0) > 5 ? '#E53E3E' : '#38A169' }}>
              {health?.errors_today || 0}
            </span>
            <span className="metric-label">{health?.errors_week || 0} this week</span>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-header">
            <XCircle size={24} />
            <h3>Critical Errors</h3>
          </div>
          <div className="health-metric">
            <span className="metric-value" style={{ color: (health?.critical_errors || 0) > 0 ? '#E53E3E' : '#38A169' }}>
              {health?.critical_errors || 0}
            </span>
            <span className="metric-label">Unresolved</span>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-header">
            <Zap size={24} />
            <h3>Automation Runs</h3>
          </div>
          <div className="health-metric">
            <span className="metric-value">{health?.automation_runs_today || 0}</span>
            <span className="metric-label">Today</span>
          </div>
        </div>

        <div className="health-card">
          <div className="health-card-header">
            <Users size={24} />
            <h3>Active Tenants</h3>
          </div>
          <div className="health-metric">
            <span className="metric-value">{health?.active_tenants_today || 0}</span>
            <span className="metric-label">Active today</span>
          </div>
        </div>
      </div>
    </div>
  );
}
